import { useRef, type KeyboardEvent } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import { inspectCopy } from '@/i18n/ns/inspect';
import { useInspectHost } from './context';

/**
 * Element → component → section, made explicit instead of guessed (INSPECTOR-SPEC §2): a
 * clicked icon is the icon unless the person picks its button. Arrow keys move along the
 * chain; Enter or a click selects that level in place of the current target. Labels come from
 * INS once resolved; a level not resolved yet reads "Element".
 */
export function NestPicker({ targetId, nodeId: own }: { targetId: string; nodeId: string }) {
  const host = useInspectHost();
  const copy = useNamespaceCopy(inspectCopy);
  const list = useRef<HTMLOListElement>(null);
  const chain = host ? host.adapter.chain(own) : [];
  // Each level's label is display text from the canonical list (Poneva's own renderer, or INS's
  // manifest for a frame): no request is spent on labels; selecting a level is a click request.
  const labels: Record<string, string> = Object.fromEntries(
    (host?.adapter.list?.() ?? []).map((n) => [n.targetId, n.label]),
  );
  if (!host || chain.length < 2) return null;
  const choose = async (nodeId: string) => {
    if (nodeId === own) return;
    const id = host.request('click');
    if (!id) return;
    const rects = host.adapter.rects(nodeId, id);
    const resolved = await host.session
      .resolve({ pid: nodeId, kind: 'element', rects, instanceRef: null }, id)
      .catch(() => null);
    if (!resolved || !host.clock.isCurrent(id, 'click')) return;
    // The parent replaces the child; a parent that is already selected stays selected.
    host.session.remove(targetId);
    if (!host.session.targets.some((t) => t.targetId === resolved.targetId))
      host.choose(resolved.targetId, 'add');
  };
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const next =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? index + 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? index - 1
          : null;
    if (next === null) return;
    event.preventDefault();
    const buttons = list.current?.querySelectorAll<HTMLButtonElement>('button');
    buttons?.[Math.max(0, Math.min(next, chain.length - 1))]?.focus();
  };
  const current = host.session.targets.find((t) => t.targetId === targetId);
  return (
    <ol
      ref={list}
      className="pc-inspect-picker"
      aria-label={copy.pickerLabel}
      data-testid="inspect-picker"
    >
      {chain.map((nodeId, i) => (
        <li key={nodeId}>
          <button
            type="button"
            className="pc-inspect-crumb"
            aria-current={nodeId === own ? 'true' : undefined}
            tabIndex={nodeId === own ? 0 : -1}
            onKeyDown={(event) => move(event, i)}
            onClick={() => void choose(nodeId)}
          >
            {nodeId === own ? (current?.label ?? copy.element) : (labels[nodeId] ?? copy.element)}
          </button>
        </li>
      ))}
    </ol>
  );
}
